import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchArchiveList,
  restoreShipment,
  restoreAllShipments,
  deleteShipment,
  setArchivePage,
  setArchiveStatusFilter,
  setArchiveOrder,
  setArchiveSearch,
} from '../../store/slices/shipmentTrackerSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import ArrowBack from '../../components/ArrowBack';
import ShipmentStatusBadge from '../../components/shipment-tracker/ShipmentStatusBadge';
import ShipmentStatusFilter from '../../components/shipment-tracker/ShipmentStatusFilter';
import ConfirmDeleteShipmentModal from '../../components/shipment-tracker/ConfirmDeleteShipmentModal';
import ConfirmRestoreAllModal from '../../components/shipment-tracker/ConfirmRestoreAllModal';
import Pagination from '../../components/Pagination';

const ShipmentTrackerArchivesPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canWrite } = useShipmentTrackerPermissions();

  const { items, total, page, limit, statusFilter, order, search, loading } =
    useAppSelector((state) => state.shipmentTracker.archive);

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showRestoreAll, setShowRestoreAll] = useState(false);
  const [restoringAll, setRestoringAll] = useState(false);

  const load = useCallback(() => {
    dispatch(
      fetchArchiveList({
        status: statusFilter.length > 0 ? statusFilter : undefined,
        q: search || undefined,
        order,
        page,
        limit,
      })
    );
  }, [dispatch, statusFilter, search, order, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = async (archiveId: string) => {
    try {
      await dispatch(restoreShipment(archiveId)).unwrap();
      dispatch(
        addNotification({ type: 'success', title: t('success'), message: t('shipment_restored') })
      );
    } catch {
      // error notification fired in thunk
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteShipment(deleteTarget.id)).unwrap();
      dispatch(
        addNotification({ type: 'success', title: t('success'), message: t('shipment_deleted') })
      );
      load();
    } catch {
      // error notification fired in thunk
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleRestoreAll = async () => {
    setRestoringAll(true);
    try {
      await dispatch(restoreAllShipments()).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('shipments_restored_all'),
        })
      );
      setShowRestoreAll(false);
    } catch {
      // error notification fired in thunk
    } finally {
      setRestoringAll(false);
    }
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    dispatch(setArchivePage(direction === 'prev' ? page - 1 : page + 1));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const isEmpty = !loading && items.length === 0 && !search && statusFilter.length === 0;

  return (
    <div className="page archives-page">
      {/* Header */}
      <div className="archives-header">
        <ArrowBack onClick={() => navigate('/features/shipment-tracker/status-board')} />
        <h1 className="page-title">{t('archives_title')}</h1>
        {canWrite && total > 0 && (
          <button className="btn btn-secondary" onClick={() => setShowRestoreAll(true)}>
            {t('archives_restore_all')}
          </button>
        )}
      </div>

      {isEmpty ? (
        /* Empty state */
        <div className="archives-empty">
          <p className="archives-empty__title">{t('archives_empty_title')}</p>
          <p className="archives-empty__body">{t('archives_empty_body')}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/features/shipment-tracker/status-board')}
          >
            {t('archives_cta_board')}
          </button>
        </div>
      ) : (
        <div className="detail-card">
          {/* Toolbar */}
          <div className="archives-toolbar">
            <input
              className="input"
              type="search"
              placeholder={t('archives_search_placeholder')}
              value={search}
              onChange={(e) => dispatch(setArchiveSearch(e.target.value))}
            />
            <select
              className="select"
              value={order}
              onChange={(e) => dispatch(setArchiveOrder(e.target.value as 'desc' | 'asc'))}
            >
              <option value="desc">{t('archives_sort_newest')}</option>
              <option value="asc">{t('archives_sort_oldest')}</option>
            </select>
            <ShipmentStatusFilter
              selected={statusFilter}
              onChange={(s) => dispatch(setArchiveStatusFilter(s))}
            />
          </div>

          {loading ? (
            <div className="archives-loading">{t('loading')}</div>
          ) : items.length === 0 ? (
            <div className="archives-empty-filtered">{t('shipment_list_empty')}</div>
          ) : (
            <div className="archives-table-scroll">
            <table className="archives-table">
              <thead>
                <tr>
                  <th>{t('archives_col_name')}</th>
                  <th>{t('archives_col_status')}</th>
                  <th>{t('archives_col_archived_at')}</th>
                  <th>{t('archives_col_archived_by')}</th>
                  {canWrite && <th>{t('archives_col_action')}</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.archiveId}>
                    <td className="archives-table__name-cell">
                      <div>{item.name}</div>
                      {item.reference && (
                        <div className="text-muted">{item.reference}</div>
                      )}
                      {item.eta && (
                        <div className="text-muted">
                          {t('shipment_col_eta')}: {item.eta}
                        </div>
                      )}
                    </td>
                    <td>
                      <ShipmentStatusBadge status={item.executionStatus ?? null} />
                    </td>
                    <td>{new Date(item.archivedAt).toLocaleDateString()}</td>
                    <td>{item.archivedByName ?? '-'}</td>
                    {canWrite && (
                      <td className="archives-table__actions">
                        <button
                          className="btn btn-link"
                          onClick={() => handleRestore(item.archiveId)}
                        >
                          {t('archives_restore')}
                        </button>
                        <button
                          className="btn btn-link btn-link--danger"
                          onClick={() =>
                            setDeleteTarget({ id: item.shipmentId, name: item.name })
                          }
                        >
                          {t('delete')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          <Pagination
            startItem={startItem}
            endItem={endItem}
            total={total}
            totalPages={totalPages}
            page={page}
            loading={loading}
            handlePageChange={handlePageChange}
          />
        </div>
      )}

      {deleteTarget && (
        <ConfirmDeleteShipmentModal
          shipmentName={deleteTarget.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}

      {showRestoreAll && (
        <ConfirmRestoreAllModal
          count={total}
          onConfirm={handleRestoreAll}
          onCancel={() => setShowRestoreAll(false)}
          loading={restoringAll}
        />
      )}
    </div>
  );
};

export default ShipmentTrackerArchivesPage;
